/**
 * Types du domaine. Aucun import : ce fichier est la racine de la hierarchie et
 * il est partage par la couche pure (`analysis/`), la base (`db/`) et l'UI.
 */

// ---------------------------------------------------------------------------
// Primitifs
// ---------------------------------------------------------------------------

export type Id = string

/**
 * Montant en **centimes de franc**, toujours entier.
 *
 * Jamais de flottant pour de l'argent : `0.1 + 0.2 !== 0.3`, et un ecart d'un
 * centime dans une repartition a trois parties suffit a rendre un justificatif
 * contestable. C'est la seule representation stockee ; la mise en forme
 * (`CHF 1'234.50`) appartient a `argent.ts` et n'existe qu'a l'affichage.
 */
export type Cents = number

/** Jour calendaire **local**, au format `YYYY-MM-DD`. */
export type DayKey = string

/** Mois calendaire **local**, au format `YYYY-MM`. */
export type MoisKey = string

/** Instant precis, en ISO 8601 UTC. Sert aux horodatages techniques
 *  (creation, derniere modification), jamais aux dates comptables. */
export type Instant = string

/** Sens d'un flux. Le montant reste toujours **positif** ; c'est le sens qui
 *  porte la direction. Stocker des negatifs obligerait chaque calcul a se
 *  souvenir du signe, et un oubli passerait inapercu. */
export type Sens = 'depense' | 'revenu'

// ---------------------------------------------------------------------------
// Comptes
// ---------------------------------------------------------------------------

export type TypeCompte = 'courant' | 'epargne' | 'joint' | 'carte' | 'especes'

export const TYPES_COMPTE: TypeCompte[] = [
  'courant',
  'epargne',
  'joint',
  'carte',
  'especes',
]

export const TYPE_COMPTE_LABELS: Record<TypeCompte, string> = {
  courant: 'Compte courant',
  epargne: 'Épargne',
  joint: 'Compte joint',
  carte: 'Carte de crédit',
  especes: 'Espèces',
}

export interface Compte {
  id: Id
  nom: string
  type: TypeCompte
  /** Solde connu a la date `soldeInitialLe`. Les operations anterieures a
   *  cette date ne sont pas recomptees : c'est le point de depart declare. */
  soldeInitial: Cents
  soldeInitialLe: DayKey
  couleur: string
  /** `1` pour archive, `0` sinon. IndexedDB refuse les booleens comme cle :
   *  un index sur un `boolean` est silencieusement omis par Dexie, et la
   *  requete correspondante renvoie toujours zero resultat sans erreur. */
  archive: 0 | 1
  ordre: number
}

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export interface Categorie {
  id: Id
  nom: string
  sens: Sens
  /** Sous-categorie si renseigne. Un seul niveau d'imbrication est prevu. */
  parentId: Id | null
  couleur: string
  archive: 0 | 1
  /**
   * Modele de repartition qui pre-remplit la cascade a la saisie.
   *
   * C'est une **suggestion**, jamais une contrainte : le partage est une
   * propriete de la depense, pas de sa categorie (deux consultations « Sante »
   * peuvent etre l'une a ma charge, l'autre partagee).
   */
  modeleParDefautId: Id | null
}

// ---------------------------------------------------------------------------
// Tiers
// ---------------------------------------------------------------------------

/** `moi` est le tiers particulier qui porte le reste a charge. Il en existe
 *  exactement un, cree a l'initialisation et non supprimable. */
export type TypeTiers = 'moi' | 'organisme' | 'personne'

export interface Tiers {
  id: Id
  nom: string
  type: TypeTiers
  /** Bloc d'adresse libre, repris tel quel en en-tete du justificatif. */
  adresse: string
  courriel: string
  archive: 0 | 1
}

// ---------------------------------------------------------------------------
// Operations
// ---------------------------------------------------------------------------

export interface Operation {
  id: Id
  sens: Sens
  date: DayKey
  /** Montant **total** sorti ou entre sur le compte, avant toute repartition.
   *  Pour la facture de dentiste avancee en entier : 400.00, pas ma quote-part. */
  montant: Cents
  libelle: string
  categorieId: Id
  compteId: Id
  note: string
  /** Constatee sur le releve bancaire. Non pointee ne veut pas dire prevue :
   *  l'operation existe et compte partout, elle n'est simplement pas encore
   *  rapprochee. */
  pointee: 0 | 1
  /** Renseignes ensemble quand l'operation materialise une echeance recurrente. */
  recurrenceId: Id | null
  occurrence: DayKey | null
  creeLe: Instant
}

// ---------------------------------------------------------------------------
// Recurrences
// ---------------------------------------------------------------------------

export type Periodicite =
  | 'hebdomadaire'
  | 'mensuel'
  | 'bimestriel'
  | 'trimestriel'
  | 'semestriel'
  | 'annuel'

export const PERIODICITES: Periodicite[] = [
  'hebdomadaire',
  'mensuel',
  'bimestriel',
  'trimestriel',
  'semestriel',
  'annuel',
]

export const PERIODICITE_LABELS: Record<Periodicite, string> = {
  hebdomadaire: 'Chaque semaine',
  mensuel: 'Chaque mois',
  bimestriel: 'Tous les 2 mois',
  trimestriel: 'Chaque trimestre',
  semestriel: 'Chaque semestre',
  annuel: 'Chaque année',
}

/** Nombre de mois entre deux echeances. `hebdomadaire` fait exception : il se
 *  compte en jours, d'ou le 0 et le traitement a part dans `recurrences.ts`. */
export const PERIODICITE_MOIS: Record<Periodicite, number> = {
  hebdomadaire: 0,
  mensuel: 1,
  bimestriel: 2,
  trimestriel: 3,
  semestriel: 6,
  annuel: 12,
}

export interface Recurrence {
  id: Id
  sens: Sens
  libelle: string
  montant: Cents
  categorieId: Id
  compteId: Id
  periodicite: Periodicite
  /** Multiplicateur de la periodicite : 2 + `mensuel` = tous les deux mois.
   *  Redondant avec `bimestriel`, mais permet les cas non prevus (tous les
   *  5 ans, toutes les 3 semaines). */
  intervalle: number
  /** Premiere echeance. C'est aussi le **jour d'ancrage** : une echeance au 31
   *  se rabat sur le 30 en avril, puis **revient au 31** en mai. Sans cet
   *  ancrage, la date deriverait definitivement des le premier mois court. */
  debut: DayKey
  fin: DayKey | null
  note: string
  actif: 0 | 1
  /** Repartition appliquee d'office aux occurrences de cette recurrence. */
  modeleRepartitionId: Id | null
}

/**
 * Ecart assume par rapport a la regle, pour **une** occurrence.
 *
 * Les occurrences ne sont pas stockees : elles se deduisent de la regle a la
 * volee. Pre-generer figerait un montant perime des la premiere hausse de prime
 * et obligerait a choisir un horizon arbitraire. Seuls les ecarts sont donc
 * materialises.
 */
export interface Exception {
  id: Id
  recurrenceId: Id
  /** Date **theorique** de l'occurrence, telle que la regle la produit. C'est
   *  la cle d'appariement : elle ne change pas quand l'operation reelle est
   *  datee d'un autre jour. */
  occurrence: DayKey
  statut: 'ignoree' | 'remplacee'
  /** L'operation reelle, quand `statut` vaut `remplacee`. */
  operationId: Id | null
}

// ---------------------------------------------------------------------------
// Repartition
// ---------------------------------------------------------------------------

/** Assiette sur laquelle la part se calcule.
 *
 *  `total` : le montant entier de la facture.
 *  `reste` : ce qui subsiste apres les parts d'ordre inferieur — c'est ce qui
 *            permet d'exprimer « moitie de ce qui reste apres remboursement »
 *            sans avoir a recalculer un pourcentage a la main. */
export type BaseePart = 'total' | 'reste'

/** `solde` absorbe le reliquat d'arrondi, afin que la somme des parts egale
 *  toujours le total au centime pres. */
export type ModePart = 'montant' | 'pourcentage' | 'solde'

export interface Part {
  id: Id
  operationId: Id
  tiersId: Id
  /** Rang dans la cascade, croissant. */
  ordre: number
  base: BaseePart
  mode: ModePart
  /** Centimes si `mode: 'montant'`, pourcentage (0-100, decimales permises) si
   *  `mode: 'pourcentage'`, ignore si `mode: 'solde'`. */
  valeur: number
  libelle: string
}

/** Une part de modele : identique a `Part`, mais rattachee a aucune operation. */
export type PartModele = Omit<Part, 'id' | 'operationId'>

export interface ModeleRepartition {
  id: Id
  nom: string
  parts: PartModele[]
}

export interface Reglement {
  id: Id
  partId: Id
  date: DayKey
  montant: Cents
  /** Compte credite. Peut differer de celui qui a avance les frais. */
  compteId: Id
  note: string
  creeLe: Instant
}

// ---------------------------------------------------------------------------
// Budgets
// ---------------------------------------------------------------------------

export interface Budget {
  id: Id
  categorieId: Id
  /** `null` = tous comptes confondus. */
  compteId: Id | null
  periode: 'mensuel' | 'annuel'
  montant: Cents
  debut: MoisKey
  fin: MoisKey | null
}

// ---------------------------------------------------------------------------
// Pieces jointes
// ---------------------------------------------------------------------------

export interface Piece {
  id: Id
  operationId: Id
  nom: string
  /** `image/jpeg` et `image/png` sont les deux seuls formats d'image que
   *  pdf-lib sait integrer : toute autre image (HEIC, WebP…) est convertie en
   *  JPEG a l'ajout. Un PDF est conserve tel quel, et ses pages sont recopiees
   *  en annexe du justificatif — les factures medicales suisses arrivent le
   *  plus souvent ainsi, par courriel. */
  type: 'image/jpeg' | 'image/png' | 'application/pdf'
  blob: Blob
  largeur: number
  hauteur: number
  octets: number
  ajouteeLe: Instant
}

// ---------------------------------------------------------------------------
// Reglages
// ---------------------------------------------------------------------------

/** Comment estimer un poste variable dans le previsionnel. */
export type ModeEstimation = 'moyenne' | 'annee-precedente' | 'auto'

export interface Reglages {
  /** Nom et adresse de l'emetteur, en en-tete des justificatifs. */
  emetteurNom: string
  emetteurAdresse: string
  /** Compte sur lequel verser le solde reclame. Sans lui, un justificatif
   *  exige un paiement sans dire ou l'effectuer. */
  emetteurIban: string
  /** Nombre de mois de recul pour la moyenne glissante du previsionnel. */
  moisMoyenne: number
  /** Au-dela de ce coefficient de variation sur 12 mois, le mode `auto` juge la
   *  categorie saisonniere et bascule sur le meme mois de l'an dernier. */
  seuilSaisonnalite: number
  modeEstimation: ModeEstimation
  /** Compte pre-selectionne a la saisie. */
  compteParDefautId: Id | null
}

export const REGLAGES_DEFAUT: Reglages = {
  emetteurNom: '',
  emetteurAdresse: '',
  emetteurIban: '',
  moisMoyenne: 3,
  seuilSaisonnalite: 0.6,
  modeEstimation: 'auto',
  compteParDefautId: null,
}

export interface MetaEntry {
  cle: string
  valeur: unknown
}
